<template>
  <el-dialog
    v-model="visible"
    width="550px"
    align-center
    append-to-body
    :close-on-click-modal="false"
    :show-close="false"
    class="skype-id-dialog"
  >
    <button type="button" class="header-close" title="close" @click="visible = false">
      <el-icon :size="20"><Close /></el-icon>
    </button>

    <p class="tip-text">
      Dear tutors，kindly update your skype ID in the system so that we can communicate with each
      other easier.
    </p>

    <div class="form-row">
      <el-input
        v-model.trim="sky"
        placeholder="Sky"
        class="sky-input"
        :class="{ 'is-error': showError }"
        @input="touched = true"
      />
      <button type="button" class="submit-btn" :disabled="loading" @click="submitSkype">
        {{ loading ? "…" : "Submit" }}
      </button>
    </div>
    <p v-if="showError" class="error-text">Required</p>
  </el-dialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { Close } from "@element-plus/icons-vue";
import { addSkypeId } from "@/api/teachinfo";
import { useConfirm } from "@/composables/useFeedback";
import { _debounce } from "@/utils/tools";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue"]);

const { alert } = useConfirm();
const sky = ref("");
const touched = ref(false);
const loading = ref(false);

const visible = computed({
  get: () => props.modelValue,
  set: (v) => emit("update:modelValue", v),
});

const showError = computed(() => touched.value && !sky.value);

watch(visible, (open) => {
  if (open) {
    sky.value = "";
    touched.value = false;
    loading.value = false;
  }
});

const submitSkype = _debounce(async () => {
  touched.value = true;
  if (!sky.value) {
    loading.value = false;
    return;
  }
  loading.value = true;
  try {
    const result = await addSkypeId({ Sky: sky.value });
    const type = result?.data?.ErrorCode === 0 ? "success" : "error";
    await alert(result?.data?.Msg || (type === "success" ? "Success" : "Failed"), { type });
    visible.value = false;
  } catch (_) {
    await alert("Failed", { type: "error" });
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped lang="scss">
.header-close {
  position: absolute;
  top: 12px;
  right: 14px;
  z-index: 2;
  border: none;
  background: transparent;
  color: #9aa0c0;
  cursor: pointer;
  padding: 4px;
  line-height: 1;

  &:hover {
    color: #32397a;
  }
}

.tip-text {
  margin: 8px 8px 20px;
  color: #32397a;
  font-size: 16px;
  line-height: 1.5;
  font-weight: 500;
}

.form-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 8px 8px;
}

.sky-input {
  flex: 1;
  min-width: 0;

  :deep(.el-input__wrapper) {
    min-height: 38px;
  }

  &.is-error :deep(.el-input__wrapper) {
    box-shadow: 0 0 0 1px #ee344c inset;
  }
}

.submit-btn {
  flex-shrink: 0;
  min-width: 88px;
  height: 38px;
  padding: 0 18px;
  border: none;
  border-radius: 4px;
  background: #ee344c;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.error-text {
  margin: 0 8px 4px;
  color: #ee344c;
  font-size: 12px;
}
</style>

<style lang="scss">
.skype-id-dialog.el-dialog {
  border-radius: 8px;
  overflow: hidden;
}

.skype-id-dialog .el-dialog__header {
  display: none;
}

.skype-id-dialog .el-dialog__body {
  padding: 28px 24px 20px;
  position: relative;
}
</style>
